import React, { useEffect, useState } from 'react';
import RCodeTable from './components/Table/RCodeTable';
import PartsSelectionFooter from './components/PartsSelectionFooter';
import { Box } from '@mui/material';
import BasicInfo from '../../_common/BasicInfo/BasicInfo';
import { AppDispatch, RootState } from '../../stores/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPartsSelection,
  updatePartsSelection,
} from '../../stores/partsSelection/slice';
import SelectSelectionModal from './components/Modal/SelectSelectionModal';
import {
  CodeInfo,
  GetRCodeParts,
  PutPartsInfo,
  PutRCodeParts,
} from '../../services/apiTypes';
import { useNavigate } from 'react-router-dom';
import {
  HISTORY_STATUSES,
  HISTORY_TYPE,
  INSPECTION_STATUSES,
  PARTS_LIST_BASE_URL,
} from '../../_constants/inspectionConst';
import { fetchRepairRequest } from '../../stores/repairRequest/slice';
import useErrorModal from '../../hooks/useErrorModal';
import ErrorModal from '../../_common/Modal/ErrorModal';
import { INSPEX_PATHNAME, PARTS_LIST_IMAGE } from '../../_constants/Const';
import { isRejected } from '@reduxjs/toolkit';
import {
  isGetPartsSelection,
  isGetRepairRequest,
} from '../../_utils/typeGuardUtils';
import { PARTS_SELECTION_CONTENTS_TOP } from '../../_constants/styleConst';
import { API_CODE } from '../../_constants/apiConst';
import useUpdateStatus from '../../hooks/useUpdateStatus';
import useSaveHistory from '../../hooks/useSaveHistory';
import SectionTable from './components/Table/SectionTable';
import { PARTS_LIST_DISPLAY_PATTERN } from '../../_constants/manageConst';
import { SectionPartInfo } from './components/Table/Section/SectionPartRow/SectionPartRow';
import { RCodePartInfo } from './components/Table/RCode/RCodePartRow/RCodePartRow';
import { ConfirmParts } from './components/Modal/ConfirmPartsModal';
import PartsListImage from './components/PartsListImage';
import NoPartsModal from './components/Modal/NoPartsModal';
import { getSortedParts } from '../../_utils/inspectionUtils';

export type RCodeInfo = {
  index: number;
  r: CodeInfo;
  partsInfo: RCodePartInfo[];
};

export type SectionInfo = {
  name: string;
  partsList: SectionPartInfo[];
};

export type PartsListImageType = {
  section: string;
  src: string;
};

const PartSelection: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { error, showApiError, showTypeError, hideError } = useErrorModal();
  const updateStatus = useUpdateStatus();
  const saveHistory = useSaveHistory();
  const { userData } = useSelector((state: RootState) => state.userInfo);
  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );
  // 選択パーツ無しのダイアログの開閉フラグ
  const [isOpenNoPartsModal, setIsOpenNoPartsModal] = useState(false);
  // セクション選択ダイアログの開閉フラグ
  const [isOpenSectionModal, setIsOpenSectionModal] = useState(false);
  // オプションパーツ表示フラグ
  const [isShowOptionParts, setIsShowOptionParts] = useState(false);
  // 重複するセクションのMap
  const [duplicateSectionMap, setDuplicateSectionMap] = useState<
    Map<string, string[]>
  >(new Map());
  // パーツリストの画像情報
  const [partsListImage, setPartsListImage] = useState<PartsListImageType>();
  // リセット用のパーツ情報(セクションの重複は排除済み)
  const [defaultPartsList, setDefaultPartsList] = useState<GetRCodeParts[]>([]);
  // 画面に表示するパーツリストのデータ(Rコード単位)
  const [rCodeList, setRCodeList] = useState<RCodeInfo[]>([]);
  // 画面に表示するパーツリストのデータ(セクション単位)
  const [sectionList, setSectionList] = useState<SectionInfo[]>([]);
  // 確認用モーダルに表示する選択済みデータ
  const [confirmPartsList, setConfirmPartsList] = useState<ConfirmParts[]>([]);

  useEffect(() => {
    (async () => {
      if (!repairRequest) {
        return;
      }
      // パーツ選択情報を取得
      const response = await dispatch(
        fetchPartsSelection({ rmaNo: repairRequest.rmaNo }),
      );
      if (isRejected(response)) {
        showApiError(response.payload, API_CODE.PARTS_SELECTION, () =>
          navigate(INSPEX_PATHNAME.RMA_SEARCH),
        );
        return;
      }
      if (!isGetPartsSelection(response.payload)) {
        showTypeError(API_CODE.PARTS_SELECTION);
        return;
      }

      const { rList } = response.payload;

      // 選択可能なパーツが1つもない場合
      if (rList.every((rInfo) => rInfo.partsInfo.length === 0)) {
        setIsOpenNoPartsModal(true);
      }

      // リセット用の初期状態を保存
      setDefaultPartsList(rList);

      // 重複したセクションをまとめる
      const duplicateSectionMap = getDuplicateSections(rList);
      if (duplicateSectionMap.size > 0) {
        setDuplicateSectionMap(duplicateSectionMap);
        setIsOpenSectionModal(true);
      } else {
        initPartsData(rList, userData.partsListDisplayPattern);
      }
    })();
  }, [repairRequest?.rmaNo]);

  /** 重複したセクションのMapを作成 */
  const getDuplicateSections = (rList: GetRCodeParts[]) => {
    // セクションのSetを作成
    const sectionSet = new Set(
      rList.flatMap((rInfo) => rInfo.partsInfo.map((part) => part.section)),
    );

    // セクションのSetをループし、先頭3文字をkeyとするMapを作成
    const tempMap = new Map<string, string[]>();
    for (const sectionName of sectionSet) {
      const key = sectionName.substring(0, 3);
      if (tempMap.has(key)) {
        tempMap.get(key)?.push(sectionName);
      } else {
        tempMap.set(key, [sectionName]);
      }
    }

    // 2つ以上同じkeyが存在するものだけを抽出
    const resultMap = new Map<string, string[]>();
    for (const [key, nameList] of tempMap.entries()) {
      if (nameList.length >= 2) {
        resultMap.set(
          key,
          // SPRING HOLDINGが上になるように並び替え
          nameList.sort((a, b) => b.localeCompare(a)),
        );
      }
    }

    return resultMap;
  };

  /** セクションの選択ダイアログを閉じた時 */
  const onCloseSelectSectionModal = (excludeSections: string[]) => {
    // セクションの重複を排除したパーツリストに更新
    const defaultRList = defaultPartsList.flatMap((rInfo) => {
      return {
        ...rInfo,
        partsInfo: rInfo.partsInfo.filter(
          (part) => !excludeSections.includes(part.section),
        ),
      };
    });

    setIsOpenSectionModal(false);
    setDefaultPartsList(defaultRList);
    initPartsData(defaultRList, userData.partsListDisplayPattern);
  };

  /** パーツリストの画像パスをセットする */
  const handlePartsListImage = (isometricDrawing: string, section: string) => {
    setPartsListImage({ section: '', src: PARTS_LIST_IMAGE.LOADING });
    const img = new Image();
    img.src = PARTS_LIST_BASE_URL + isometricDrawing;
    img.onload = () => {
      setPartsListImage({ section, src: img.src });
    };
    img.onerror = () => {
      setPartsListImage({ section: '', src: PARTS_LIST_IMAGE.ERROR });
    };
  };

  /** ユーザー設定に応じたパーツリストの初期化 */
  const initPartsData = (rList: GetRCodeParts[], displayPattern: number) => {
    /** セクション単位で表示 */
    if (displayPattern === PARTS_LIST_DISPLAY_PATTERN.SECTION.id) {
      // 全パーツからセクション名のSetを作成
      const sectionNameSet = new Set(
        rList.flatMap((r) => r.partsInfo.map((part) => part.section)),
      );

      // パーツとRコードを1対1で紐づけたオブジェクト
      const flatPartsList = rList
        .map((rInfo) => {
          return rInfo.partsInfo.map((part) => {
            return {
              ...part,
              r: { code: rInfo.r.code, detail: rInfo.r.detail },
            };
          });
        })
        .flat()
        // Rコードの昇順に並び替え
        .sort((a, b) => Number(a.r.code.slice(1)) - Number(b.r.code.slice(1)));

      // セクションごとのデータにしてセットする
      setSectionList(
        Array.from(sectionNameSet).map((sectionName, sectionIndex) => {
          const sectionData: SectionInfo = { name: sectionName, partsList: [] };
          flatPartsList.forEach((part, partIndex) => {
            if (part.section !== sectionName) {
              return;
            }
            // 先頭のセクション内の先頭パーツのアイソメ図をデフォルトで表示
            if (sectionIndex === 0 && partIndex === 0) {
              handlePartsListImage(part.isometricDrawing, sectionName);
            }
            // リストNo.と図中番号が同じものを同一パーツとする
            const duplicatePart = sectionData.partsList.find(
              (p) =>
                p.listNo === part.listNo && p.inFigureNo === part.inFigureNo,
            );
            // 同一パーツはまとめて1行に表示
            // Rコード、Rコードの詳細は改行して全て表示
            if (duplicatePart) {
              duplicatePart.r.push({
                code: part.r.code,
                detail: part.r.detail,
              });
              return;
            }
            sectionData.partsList.push({ ...part, r: [part.r] });
          });
          return sectionData;
        }),
      );
    } else {
      /** Rコード単位で表示 */
      setRCodeList(
        rList.map((rInfo) => {
          return {
            index: rInfo.index,
            r: rInfo.r,
            partsInfo: rInfo.partsInfo.map((part) => {
              return { ...part, isDuplicate: false };
            }),
          };
        }),
      );
    }
  };

  /** リセット実行 */
  const handleReset = () => {
    initPartsData(defaultPartsList, userData.partsListDisplayPattern);
  };

  /** 次へボタン押下時、確認ダイアログ用にデータを変換 */
  const convertToConfirmPartsList = () => {
    const confirmParts: ConfirmParts[] = [];
    // セクション単位
    if (
      userData.partsListDisplayPattern === PARTS_LIST_DISPLAY_PATTERN.SECTION.id
    ) {
      confirmParts.push(
        ...sectionList.flatMap((section, sectionIndex) =>
          section.partsList
            .filter((part) => part.isSelect)
            .map((part, partIndex) => {
              return {
                id: sectionIndex * 10 + partIndex,
                partsCode: part.selectedPartsCode,
                partsName:
                  part.partsList.find(
                    (p) => p.partsCode === part.selectedPartsCode,
                  )?.name ?? '',
                amount: part.amount,
                section: section.name,
                // 重複しているパーツは先頭のRコードで選択したことにする
                rCode: part.r[0].code,
                rCodeDetail: part.r[0].detail,
              };
            }),
        ),
      );
    } else {
      // Rコード単位
      confirmParts.push(
        ...rCodeList.flatMap((rInfo, rIndex) =>
          rInfo.partsInfo
            .filter((part) => part.isSelect)
            .map((part, partIndex) => {
              return {
                id: rIndex * 10 + partIndex,
                partsCode: part.selectedPartsCode,
                partsName:
                  part.partsList.find(
                    (p) => p.partsCode === part.selectedPartsCode,
                  )?.name ?? '',
                amount: part.amount,
                section: part.section,
                rCode: rInfo.r.code,
                rCodeDetail: rInfo.r.detail,
              };
            }),
        ),
      );
    }
    // 並び替え(Rコード × 部品コード)
    setConfirmPartsList(getSortedParts(confirmParts));
  };

  /** 戻るボタン押下時 */
  const onClickBack = async () => {
    if (!repairRequest) {
      return;
    }

    const isSuccess = await updateStatus(
      INSPECTION_STATUSES.INSPECTION_CONFIRM,
      0,
      showApiError,
    );
    if (isSuccess) {
      navigate(INSPEX_PATHNAME.CONFIRMED_INSPECTION);
    }
  };

  /** 表示用のデータから保存用のデータを作成 */
  const createSaveData: () => PutRCodeParts[] = () => {
    // セクション単位
    if (
      userData.partsListDisplayPattern === PARTS_LIST_DISPLAY_PATTERN.SECTION.id
    ) {
      // Rコードの重複を解除したパーツリストを作成
      const partsList: (PutPartsInfo & { r: CodeInfo })[] = sectionList.flatMap(
        (section) =>
          section.partsList.flatMap((part) =>
            part.r.map((rInfo, rIndex) => ({
              r: rInfo,
              listNo: part.listNo,
              inFigureNo: part.inFigureNo,
              // 重複しているパーツは先頭のRコードで選択したことにする
              isSelect: part.isSelect && rIndex === 0,
              selectedPartsCode: part.selectedPartsCode,
              amount: part.amount,
            })),
          ),
      );
      // indexは変更不可なので、defaultPartsListをベースにRコード単位のデータを作成
      return defaultPartsList.map(({ index, r }) => ({
        index,
        r,
        partsInfo: partsList
          .filter((part) => part.r.code === r.code) // 該当Rコードのみ抽出
          .map(({ r, ...putPartsInfo }) => putPartsInfo), // PutPartsInfo型のデータのみ抽出
      }));
    } else {
      // Rコード単位
      return rCodeList.map(({ index, r, partsInfo }) => ({
        index,
        r,
        partsInfo: partsInfo.map((part) => ({
          listNo: part.listNo,
          inFigureNo: part.inFigureNo,
          isSelect: part.isSelect,
          selectedPartsCode: part.selectedPartsCode,
          amount: part.amount,
        })),
      }));
    }
  };

  /** 確認モーダルのOK押下時 */
  const handleComplete = async () => {
    if (!repairRequest) {
      return;
    }

    const saveData = createSaveData();
    const partSelectionResponse = await dispatch(
      updatePartsSelection({ rmaNo: repairRequest.rmaNo, rList: saveData }),
    );
    if (isRejected(partSelectionResponse)) {
      showApiError(partSelectionResponse.payload, API_CODE.PARTS_SELECTION);
      return;
    }
    // ステータスの更新はBE側で行う
    // 帳票画面を表示できるように更新後のステータスを取得する
    const repairRequestResponse = await dispatch(
      fetchRepairRequest({ rmaNo: repairRequest.rmaNo }),
    );
    if (isRejected(repairRequestResponse)) {
      showApiError(repairRequestResponse.payload, API_CODE.REPAIR_REQUEST);
      return;
    } else if (!isGetRepairRequest(repairRequestResponse.payload)) {
      showTypeError(API_CODE.REPAIR_REQUEST);
      return;
    }
    /** 操作履歴(パーツ選択のCompletion)を保存 */
    saveHistory(
      repairRequest,
      HISTORY_STATUSES.PARTS_SELECTION.id,
      HISTORY_TYPE.COMPLETION.id,
      () =>
        navigate(INSPEX_PATHNAME.REPORT, {
          state: {
            defaultQcCount: 0,
          },
        }),
      showApiError,
      JSON.stringify(saveData),
    );
  };

  return (
    <>
      <BasicInfo />
      <Box
        width="95%"
        sx={{
          paddingTop: PARTS_SELECTION_CONTENTS_TOP,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        <Box display="flex">
          <Box sx={{ minWidth: partsListImage ? '62%' : '100%' }}>
            {userData.partsListDisplayPattern ===
            PARTS_LIST_DISPLAY_PATTERN.SECTION.id ? (
              <SectionTable
                sectionList={sectionList}
                isShowOptionParts={isShowOptionParts}
                setSectionList={setSectionList}
                handlePartsListImage={handlePartsListImage}
              />
            ) : (
              <RCodeTable
                rCodeList={rCodeList}
                isShowOptionParts={isShowOptionParts}
                setRCodeList={setRCodeList}
                handlePartsListImage={handlePartsListImage}
              />
            )}
            <PartsSelectionFooter
              isSelectableParts={
                !defaultPartsList.every((rInfo) => rInfo.partsInfo.length === 0)
              }
              partsList={confirmPartsList}
              isShowOptionParts={isShowOptionParts}
              setIsShowOptionParts={setIsShowOptionParts}
              onClickBack={onClickBack}
              onClickReset={handleReset}
              handleComplete={handleComplete}
              convertToConfirmPartsList={convertToConfirmPartsList}
            />
          </Box>
          <PartsListImage
            partsListImage={partsListImage}
            setPartsListImage={setPartsListImage}
          />
        </Box>
        <NoPartsModal
          isOpen={isOpenNoPartsModal}
          onClose={() => setIsOpenNoPartsModal(false)}
        />
        <SelectSelectionModal
          isOpen={isOpenSectionModal}
          sectionMap={duplicateSectionMap}
          onClose={onCloseSelectSectionModal}
        />
      </Box>
      {error && <ErrorModal message={error} onClose={hideError} />}
    </>
  );
};

export default PartSelection;
